import { useCallback, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { useOrganizationStore } from "../../../../store/organization";
import { classNames } from "../../../../utils";
import {
  MXCsvRecord,
  ReconciliationResponse,
  ReconciliationTab,
  ReconciliationSummary,
  LocationBreakdown,
  MultiEventEntry,
  MultiEventResult,
} from "./types";
import {
  matchedColumns,
  ghostDeclinedColumns,
  ghostApprovedColumns,
  orphanMXColumns,
  orphanRoninColumns,
  idMismatchedColumns,
  duplicateOrdersColumns,
  doubleChargeColumns,
  voidedColumns,
  duplicateMXColumns,
  statusMismatchColumns,
  findStatusMismatches,
  parseReferenceId,
  postStaged,
  formatCents,
  getProcessorLabels,
  applyProcessorLabel,
  downloadFixItCsv,
} from "./helpers";
import { MXUploadModal } from "./MXUploadModal";
import { ReconciliationSummaryPanel } from "./ReconciliationSummary";
import { GenerateSQLModal } from "./GenerateSQLModal";

const API_BASE =
  (import.meta as any).env?.VITE_RONIN_API_BASE ?? "https://api.roninpos.app";

const TABS: { id: ReconciliationTab; label: string }[] = [
  { id: "summary", label: "Summary" },
  { id: "matched", label: "Matched" },
  { id: "ghostDeclined", label: "Ghost Declined" },
  { id: "ghostApproved", label: "Ghost Approved" },
  { id: "orphans", label: "Orphans" },
  { id: "idMismatched", label: "ID Mismatched" },
  { id: "duplicates", label: "Duplicates" },
  { id: "doubleCharges", label: "Double Charges" },
  { id: "refundMismatches", label: "Refund Mismatches" },
  { id: "voided", label: "Voided" },
  { id: "duplicateMX", label: "Duplicate MX Rows" },
  { id: "statusMismatches", label: "Status Mismatches" },
];

// Contextual descriptions for each tab — `proc` is the processor display
// name for the current reconciliation ("Stripe", "MX", or "Processor").
const tabDescriptions = (proc: string): Record<string, { text: string; source: "mx" | "ronin" | "both" | "none" }> => ({
  matched:       { text: `Transactions found in both the ${proc} CSV and Ronin with matching amounts.`, source: "both" },
  ghostDeclined: { text: `${proc} shows these as declined, but Ronin's payment went through (approved). Money moved despite the decline.`, source: "ronin" },
  ghostApproved: { text: `${proc} shows these as approved, but Ronin's payment was declined/failed. Customer may have been charged without Ronin knowing.`, source: "mx" },
  orphans:       { text: "Transactions that exist in one system but not the other.", source: "none" },
  idMismatched:  { text: "Orders we have on both sides but the reference IDs don't match — paired by amount and last 4 of card.", source: "both" },
  duplicates:    { text: "Duplicate Ronin orders — two orders for one Stripe charge (same PaymentIntent). Void the 'dup' order; keep the one that owns the charge.", source: "ronin" },
  doubleCharges: { text: "One reference charged 2+ times in Stripe — a real customer double-charge. Keep one charge and refund the extra(s).", source: "mx" },
  refundMismatches: { text: `All orders where Ronin order status is 'refund'. Mismatches are where payments.amount still holds the original charge — ${proc} shows the refund but Ronin's payment was never updated.`, source: "ronin" },
  voided:        { text: "Orders in Ronin that were voided or cancelled.", source: "ronin" },
  duplicateMX:   { text: `Transactions that appear in more than one uploaded CSV file with the same amount and status. These inflate the ${proc} total and are not real discrepancies.`, source: "mx" },
  statusMismatches: { text: "Records where the Order, Payment, and Transaction statuses disagree — something in the processing chain didn't resolve consistently.", source: "ronin" },
});

/* ------------------------------------------------------------------ */
/* Shared UI pieces                                                   */
/* ------------------------------------------------------------------ */

function Card({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden ${className}`}>
      {children}
    </div>
  );
}

function CardHeader({ children, right }: { children: React.ReactNode; right?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-gray-50">
      <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">{children}</div>
      {right && <div>{right}</div>}
    </div>
  );
}

function SourcePill({ system, label = "Processor" }: { system: "mx" | "ronin"; label?: string }) {
  return system === "mx" ? (
    <span className="inline-flex items-center rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-semibold text-orange-700 uppercase tracking-wide">{label}</span>
  ) : (
    <span className="inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold text-indigo-700 uppercase tracking-wide">Ronin</span>
  );
}

function TabDescription({ tab, proc }: { tab: ReconciliationTab; proc: string }) {
  const info = tabDescriptions(proc)[tab];
  if (!info) return null;
  return (
    <div className="flex items-center gap-2 px-4 py-2 text-xs text-gray-500 border-b border-gray-100">
      {info.source === "mx" && <><SourcePill system="mx" label={proc} /><span className="text-gray-300">→</span><span className="text-gray-300 line-through text-[10px]">Ronin</span></>}
      {info.source === "ronin" && <><span className="text-gray-300 line-through text-[10px]">{proc}</span><span className="text-gray-300">→</span><SourcePill system="ronin" /></>}
      {info.source === "both" && <><SourcePill system="mx" label={proc} /><span className="text-gray-300">=</span><SourcePill system="ronin" /></>}
      <span>{info.text}</span>
    </div>
  );
}

/** Toggle button group — replaces the old rounded pill toggles */
function ToggleGroup({ options, value, onChange }: {
  options: { id: string; label: string; count?: number; accent?: string }[];
  value: string;
  onChange: (id: string) => void;
}) {
  return (
    <div className="flex gap-1 rounded-lg bg-gray-100 p-0.5">
      {options.map((opt) => {
        const isActive = value === opt.id;
        return (
          <button
            key={opt.id}
            className={classNames(
              "rounded-md px-3 py-1.5 text-xs font-medium transition-colors tabular-nums",
              isActive
                ? `bg-white shadow-sm ${opt.accent ?? "text-gray-800"}`
                : "text-gray-500 hover:text-gray-700"
            )}
            onClick={() => onChange(opt.id)}
          >
            {opt.label}
            {opt.count != null && <span className="ml-1 text-gray-400">({opt.count.toLocaleString()})</span>}
          </button>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Main component                                                     */
/* ------------------------------------------------------------------ */

export function MXMerchantReconciliation() {
  const { organizationId } = useOrganizationStore();

  const [showUpload, setShowUpload] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ReconciliationResponse | null>(null);
  const [activeTab, setActiveTab] = useState<ReconciliationTab>("summary");
  const [orphanSub, setOrphanSub] = useState<"mx" | "ronin">("mx");
  const [matchedFilter, setMatchedFilter] = useState<"all" | "differences">("differences");
  const [refundFilter, setRefundFilter] = useState<"mismatches" | "all">("mismatches");
  const [reconEventId, setReconEventId] = useState<number | null>(null);
  const [reconEventName, setReconEventName] = useState<string | null>(null);
  const [reconProcessors, setReconProcessors] = useState<string[]>([]);
  const [showSQLModal, setShowSQLModal] = useState(false);

  // --- Multi-event state ---
  const [multiEventResults, setMultiEventResults] = useState<Map<number, MultiEventResult> | null>(null);
  const [selectedEventId, setSelectedEventId] = useState<number | "all">("all");
  const [multiProgress, setMultiProgress] = useState<{ current: number; total: number } | null>(null);

  const gridRef = useRef<AgGridReact>(null);

  // Processor-aware wording: "Stripe" / "MX" when all uploaded files came
  // from one processor, generic "Processor" / "Proc." when mixed.
  const { short: procShort, name: procName } = useMemo(
    () => getProcessorLabels(reconProcessors),
    [reconProcessors]
  );

  // Column defs with headers re-labelled for the active processor
  // (e.g. "MX Status" → "Stripe Status").
  const cols = useMemo(() => ({
    matched: applyProcessorLabel(matchedColumns, procShort),
    ghostDeclined: applyProcessorLabel(ghostDeclinedColumns, procShort),
    ghostApproved: applyProcessorLabel(ghostApprovedColumns, procShort),
    orphanMX: applyProcessorLabel(orphanMXColumns, procShort),
    idMismatched: applyProcessorLabel(idMismatchedColumns, procShort),
    voided: applyProcessorLabel(voidedColumns, procShort),
    duplicateMX: applyProcessorLabel(duplicateMXColumns, procShort),
    statusMismatch: applyProcessorLabel(statusMismatchColumns, procShort),
  }), [procShort]);

  const handleUpload = useCallback(
    async (csvRecords: MXCsvRecord[], selectedEventId: number, eventName: string, dateFrom?: string, dateTo?: string, processorNames?: string[]) => {
      setLoading(true);
      setError(null);
      setData(null);
      setMultiEventResults(null);
      try {
        const token = localStorage.getItem("ron-auth");
        const memoByRow = new Map<number, string>();
        for (const r of csvRecords) {
          if (r._rowIndex != null && r.memo) memoByRow.set(r._rowIndex, r.memo);
        }
        // Rows go up and the answer comes back in pieces — a big event is far
        // past Vercel's 4.5 MB cap both ways (RP-2535, helpers.tsx → postStaged).
        const result = await postStaged<ReconciliationResponse>(
          API_BASE,
          "/api/mx-merchant-reconciliation",
          // NOTE: memo is intentionally NOT sent. The Memo column holds
          // the full localOrder JSON (~1.6 KB median, up to 30 KB) and on
          // a busy event the round-tripped payload balloons to ~360 MB raw
          // / ~17 MB gzipped — past Vercel's 4.5 MB edge cap, and big
          // enough to OOM `vercel dev` locally with a 500. We don't need
          // it on the server: parseReferenceId(r.invoice, r.memo) runs
          // here on the client (the helpers mirror the server's parser
          // byte-for-byte), and the memoByRow map below stitches memos
          // back into orphan MX rows after the response returns.
          csvRecords.map(r => ({
            reference_id: parseReferenceId(r.invoice, r.memo),
            amount: r.amount,
            status: r.status,
            date: r.date,
            type: r.type,
            last4: r.last4,
            cardType: r.cardType,
            invoice: r.invoice,
            paymentIntentId: r.paymentIntentId,
            _rowIndex: r._rowIndex,
            _fileIndex: r._fileIndex,
          })),
          { eventId: selectedEventId, organizationId, dateFrom, dateTo },
          token
        );
        for (const o of result.orphanMX) {
          if (o._rowIndex != null && memoByRow.has(o._rowIndex)) {
            o.memo = memoByRow.get(o._rowIndex)!;
          }
        }
        setData(result);
        setActiveTab("summary");
        setReconEventId(selectedEventId);
        setReconEventName(eventName);
        setReconProcessors(processorNames ?? []);
      } catch (err: any) {
        setError(err.message || "Reconciliation failed");
      } finally {
        setLoading(false);
      }
    },
    [organizationId]
  );

  const handleUploadAll = useCallback(
    async (events: MultiEventEntry[], processorNames?: string[]) => {
      setLoading(true);
      setError(null);
      setData(null);
      setMultiEventResults(null);
      setMultiProgress({ current: 0, total: events.length });
      try {
        const token = localStorage.getItem("ron-auth");
        const results = new Map<number, MultiEventResult>();
        // Fire all API calls in parallel
        const promises = events.map(async (entry, idx) => {
          const memoByRow = new Map<number, string>();
          for (const r of entry.records) {
            if (r._rowIndex != null && r.memo) memoByRow.set(r._rowIndex, r.memo);
          }
          // memo intentionally omitted — see handleUpload above for why.
          // Sent and returned in pieces (RP-2535, helpers.tsx → postStaged).
          const result = await postStaged<ReconciliationResponse>(
            API_BASE,
            "/api/mx-merchant-reconciliation",
            entry.records.map(r => ({
              reference_id: parseReferenceId(r.invoice, r.memo),
              amount: r.amount,
              status: r.status,
              date: r.date,
              type: r.type,
              last4: r.last4,
              cardType: r.cardType,
              invoice: r.invoice,
              paymentIntentId: r.paymentIntentId,
              _rowIndex: r._rowIndex,
              _fileIndex: r._fileIndex,
            })),
            { eventId: entry.eventId, organizationId, dateFrom: entry.dateFrom, dateTo: entry.dateTo },
            token
          ).catch((err: Error) => {
            throw new Error(`${err.message} for ${entry.eventName}`);
          });
          // Attach memo data to orphanMX records
          for (const o of result.orphanMX) {
            if (o._rowIndex != null && memoByRow.has(o._rowIndex)) {
              o.memo = memoByRow.get(o._rowIndex)!;
            }
          }
          setMultiProgress(prev => prev ? { ...prev, current: prev.current + 1 } : null);
          return { eventId: entry.eventId, eventName: entry.eventName, data: result };
        });
        const settled = await Promise.all(promises);
        for (const r of settled) {
          results.set(r.eventId, { data: r.data, eventName: r.eventName });
        }
        setMultiEventResults(results);
        setSelectedEventId("all");
        setActiveTab("summary");
        setReconProcessors(processorNames ?? []);
        setReconEventId(null);
        setReconEventName(null);
      } catch (err: any) {
        setError(err.message || "Multi-event reconciliation failed");
      } finally {
        setLoading(false);
        setMultiProgress(null);
      }
    },
    [organizationId]
  );

  const handleExport = useCallback(() => {
    gridRef.current?.api?.exportDataAsCsv({ fileName: `${procName.toLowerCase()}-reconciliation-${activeTab}.csv` });
  }, [activeTab, procName]);

  // --- Merge all multi-event responses into a combined ReconciliationResponse ---
  const activeData: ReconciliationResponse | null = useMemo(() => {
    // Single-event mode: use `data` directly
    if (!multiEventResults) return data;
    if (multiEventResults.size === 0) return null;

    if (selectedEventId !== "all") {
      const entry = multiEventResults.get(selectedEventId as number);
      return entry?.data ?? null;
    }

    // Merge all responses for "All Events" view
    const allResponses = Array.from(multiEventResults.values()).map(r => r.data);
    const eventNames = Array.from(multiEventResults.entries());
    const nameMap = new Map(eventNames.map(([id, r]) => [id, r.eventName]));

    // Concat arrays — add event_name to records for identification
    const addEventName = <T extends Record<string, any>>(arr: T[], eventName: string): (T & { event_name?: string })[] =>
      arr.map(r => ({ ...r, event_name: eventName }));

    let matched: any[] = [];
    let ghostDeclined: any[] = [];
    let ghostApproved: any[] = [];
    let orphanMX: any[] = [];
    let orphanRonin: any[] = [];
    let idMismatched: any[] = [];
    let voidedOrders: any[] = [];
    let duplicateMX: any[] = [];
    let doubleCharges: any[] = [];

    for (const [eventId, entry] of multiEventResults) {
      const name = entry.eventName;
      matched = matched.concat(addEventName(entry.data.matched, name));
      ghostDeclined = ghostDeclined.concat(addEventName(entry.data.ghostDeclined, name));
      ghostApproved = ghostApproved.concat(addEventName(entry.data.ghostApproved, name));
      orphanMX = orphanMX.concat(addEventName(entry.data.orphanMX, name));
      orphanRonin = orphanRonin.concat(addEventName(entry.data.orphanRonin, name));
      idMismatched = idMismatched.concat(addEventName(entry.data.idMismatched ?? [], name));
      voidedOrders = voidedOrders.concat(addEventName(entry.data.voidedOrders, name));
      duplicateMX = duplicateMX.concat(addEventName(entry.data.duplicateMX ?? [], name));
      doubleCharges = doubleCharges.concat(addEventName(entry.data.doubleCharges ?? [], name));
    }

    // Sum summary numeric fields
    const summaries = allResponses.map(r => r.summary);
    const sumField = (field: keyof ReconciliationSummary) =>
      summaries.reduce((s, sum) => s + ((sum[field] as number) || 0), 0);

    const totalMatched = sumField("matched_count");
    const totalMxApproved = sumField("mx_approved_total_cents");
    const totalRoninProcessed = sumField("ronin_processed_total_cents");
    const matchRate = totalMxApproved > 0
      ? Math.round((totalMatched / (totalMatched + sumField("orphan_mx_count") + sumField("orphan_ronin_count"))) * 10000) / 100
      : 0;

    // Merge location_breakdown across events
    const locMap = new Map<string, LocationBreakdown>();
    for (const sum of summaries) {
      for (const loc of sum.location_breakdown ?? []) {
        const existing = locMap.get(loc.location_name);
        if (existing) {
          existing.matched_count += loc.matched_count;
          existing.matched_total_cents += loc.matched_total_cents;
          existing.ghost_declined_count += loc.ghost_declined_count;
          existing.ghost_declined_total_cents += loc.ghost_declined_total_cents;
          existing.ghost_approved_count += loc.ghost_approved_count;
          existing.ghost_approved_total_cents += loc.ghost_approved_total_cents;
          existing.voided_count += loc.voided_count;
          existing.voided_total_cents += loc.voided_total_cents;
        } else {
          locMap.set(loc.location_name, { ...loc });
        }
      }
    }

    // Merge excluded_event_breakdown
    const excludedMap = new Map<string, { event_name: string; count: number; total_cents: number }>();
    for (const sum of summaries) {
      for (const eb of sum.excluded_event_breakdown ?? []) {
        const existing = excludedMap.get(eb.event_name);
        if (existing) {
          existing.count += eb.count;
          existing.total_cents += eb.total_cents;
        } else {
          excludedMap.set(eb.event_name, { ...eb });
        }
      }
    }

    const mergedSummary: ReconciliationSummary = {
      mx_approved_total_cents: totalMxApproved,
      ronin_processed_total_cents: totalRoninProcessed,
      ronin_merchant_processed_cents: sumField("ronin_merchant_processed_cents"),
      ronin_financial_breakdown: {
        gross_credit_sales: summaries.reduce((s, sum) => s + sum.ronin_financial_breakdown.gross_credit_sales, 0),
        gross_credit_tips: summaries.reduce((s, sum) => s + sum.ronin_financial_breakdown.gross_credit_tips, 0),
        gross_rfid_sales: summaries.reduce((s, sum) => s + sum.ronin_financial_breakdown.gross_rfid_sales, 0),
        gross_rfid_tips: summaries.reduce((s, sum) => s + sum.ronin_financial_breakdown.gross_rfid_tips, 0),
        total_digital_surcharge: summaries.reduce((s, sum) => s + sum.ronin_financial_breakdown.total_digital_surcharge, 0),
        total_discounts: summaries.reduce((s, sum) => s + sum.ronin_financial_breakdown.total_discounts, 0),
        total_promo_discounts: summaries.reduce((s, sum) => s + sum.ronin_financial_breakdown.total_promo_discounts, 0),
        total_refunds: summaries.reduce((s, sum) => s + sum.ronin_financial_breakdown.total_refunds, 0),
      },
      gap_cents: sumField("gap_cents"),
      financial_gap_cents: sumField("financial_gap_cents"),
      matched_count: totalMatched,
      matched_difference_count: sumField("matched_difference_count"),
      exact_match_count: sumField("exact_match_count"),
      match_rate_percent: matchRate,
      rescued_outside_date_range_count: sumField("rescued_outside_date_range_count"),
      ghost_declined_total_cents: sumField("ghost_declined_total_cents"),
      ghost_declined_count: sumField("ghost_declined_count"),
      ghost_approved_total_cents: sumField("ghost_approved_total_cents"),
      ghost_approved_count: sumField("ghost_approved_count"),
      orphan_mx_count: sumField("orphan_mx_count"),
      orphan_mx_total_cents: sumField("orphan_mx_total_cents"),
      orphan_ronin_count: sumField("orphan_ronin_count"),
      orphan_ronin_total_cents: sumField("orphan_ronin_total_cents"),
      id_mismatched_count: sumField("id_mismatched_count"),
      gap_matched_cents: sumField("gap_matched_cents"),
      gap_ghost_declined_cents: sumField("gap_ghost_declined_cents"),
      gap_ghost_approved_cents: sumField("gap_ghost_approved_cents"),
      gap_orphan_mx_cents: sumField("gap_orphan_mx_cents"),
      gap_orphan_ronin_cents: sumField("gap_orphan_ronin_cents"),
      gap_id_mismatched_cents: sumField("gap_id_mismatched_cents"),
      gap_voided_cents: sumField("gap_voided_cents"),
      gap_excluded_cents: sumField("gap_excluded_cents"),
      excluded_other_event_count: sumField("excluded_other_event_count"),
      excluded_event_breakdown: excludedMap.size > 0 ? Array.from(excludedMap.values()) : undefined,
      voided_count: sumField("voided_count"),
      voided_still_charged_count: sumField("voided_still_charged_count"),
      voided_still_charged_total_cents: sumField("voided_still_charged_total_cents"),
      voided_both_agree_count: sumField("voided_both_agree_count"),
      refund_mismatch_count: sumField("refund_mismatch_count"),
      refund_mismatch_ronin_cents: sumField("refund_mismatch_ronin_cents"),
      refund_mismatch_mx_cents: sumField("refund_mismatch_mx_cents"),
      gap_refund_mismatch_cents: sumField("gap_refund_mismatch_cents"),
      duplicate_mx_count: sumField("duplicate_mx_count"),
      duplicate_mx_total_cents: sumField("duplicate_mx_total_cents"),
      duplicate_mx_approved_cents: sumField("duplicate_mx_approved_cents"),
      location_breakdown: locMap.size > 0 ? Array.from(locMap.values()) : undefined,
    };

    return {
      summary: mergedSummary,
      matched,
      ghostDeclined,
      ghostApproved,
      orphanMX,
      orphanRonin,
      idMismatched,
      voidedOrders,
      duplicateMX,
      doubleCharges,
    } as ReconciliationResponse;
  }, [data, multiEventResults, selectedEventId]);

  const statusMismatches = useMemo(() => {
    if (!activeData) return [];
    return findStatusMismatches(activeData);
  }, [activeData]);

  // Duplicate Ronin orders: the "dup charge" subset of ID Mismatched — a Ronin
  // order whose Stripe charge already belongs to a different (keeper) order.
  const duplicateOrders = useMemo(() => {
    if (!activeData) return [];
    return (activeData.idMismatched ?? []).filter(m => m.matched_by === "PaymentIntent (dup charge)");
  }, [activeData]);

  const allRefunds = useMemo(() => {
    if (!activeData) return [];
    return activeData.matched.filter(m => {
      const orderStatus = (m.ronin_status || "").toLowerCase();
      return orderStatus === "refund";
    });
  }, [activeData]);

  const refundMismatches = useMemo(() => {
    return allRefunds.filter(m => {
      const payStatus = (m.payment_status || "").toLowerCase();
      return payStatus !== "refund" && payStatus !== "refunded";
    });
  }, [allRefunds]);

  const badgeCounts = useMemo(() => {
    if (!activeData) return {};
    return {
      matched: activeData.matched.length,
      ghostDeclined: activeData.ghostDeclined.length,
      ghostApproved: activeData.ghostApproved.length,
      orphans: activeData.orphanMX.length + activeData.orphanRonin.length,
      idMismatched: activeData.idMismatched?.length ?? 0,
      duplicates: duplicateOrders.length,
      doubleCharges: activeData.doubleCharges?.length ?? 0,
      refundMismatches: allRefunds.length,
      voided: activeData.voidedOrders.length,
      duplicateMX: activeData.duplicateMX?.length ?? 0,
      statusMismatches: statusMismatches.length,
    } as Record<string, number>;
  }, [activeData, statusMismatches, allRefunds, duplicateOrders]);

  const defaultColDef = { sortable: true, resizable: true, filter: true };
  const sharedGridProps = { enableCellTextSelection: true, ensureDomOrder: true } as const;

  const matchedDifferences = useMemo(() => {
    if (!activeData) return [];
    return activeData.matched.filter(m => m.difference_cents !== 0);
  }, [activeData]);

  /* ---------------------------------------------------------------- */
  /* Grid wrappers — each tab's content, wrapped in Card              */
  /* ---------------------------------------------------------------- */

  const renderGrid = () => {
    if (!activeData) return null;

    switch (activeTab) {
      case "summary":
        return <ReconciliationSummaryPanel summary={activeData.summary} processorLabel={procName} />;

      case "matched": {
        const filteredMatched = matchedFilter === "differences" ? matchedDifferences : activeData.matched;
        const netDiff = matchedDifferences.reduce((s, m) => s + m.difference_cents, 0);
        return (
          <Card>
            <CardHeader
              right={
                <ToggleGroup
                  value={matchedFilter}
                  onChange={(v) => setMatchedFilter(v as "all" | "differences")}
                  options={[
                    { id: "differences", label: "Differences Only", count: matchedDifferences.length, accent: "text-orange-700" },
                    { id: "all", label: "All Matched", count: activeData.matched.length },
                  ]}
                />
              }
            >
              Matched Transactions
            </CardHeader>
            <TabDescription tab={activeTab} proc={procName} />
            {matchedFilter === "differences" && matchedDifferences.length > 0 && (
              <div className="px-4 py-2 text-xs text-gray-500 border-b border-gray-100">
                Net: <span className="font-mono font-medium text-orange-600 tabular-nums">
                  {netDiff < 0 ? "−" : "+"}${(Math.abs(netDiff) / 100).toFixed(2)}
                </span>
                {" "}across {matchedDifferences.length} records — Ronin records {netDiff < 0 ? "higher" : "lower"} amounts than {procShort}
              </div>
            )}
            <div className="ag-theme-quartz">
              <AgGridReact {...sharedGridProps} ref={gridRef} rowData={filteredMatched} columnDefs={cols.matched} defaultColDef={defaultColDef} domLayout="autoHeight" pagination paginationPageSize={50} />
            </div>
          </Card>
        );
      }

      case "ghostDeclined":
        return (
          <Card>
            <CardHeader>Ghost Declined</CardHeader>
            <TabDescription tab={activeTab} proc={procName} />
            <div className="ag-theme-quartz">
              <AgGridReact {...sharedGridProps} ref={gridRef} rowData={activeData.ghostDeclined} columnDefs={cols.ghostDeclined} defaultColDef={defaultColDef} domLayout="autoHeight" pagination paginationPageSize={50} />
            </div>
          </Card>
        );

      case "ghostApproved":
        return (
          <Card>
            <CardHeader>Ghost Approved</CardHeader>
            <TabDescription tab={activeTab} proc={procName} />
            <div className="ag-theme-quartz">
              <AgGridReact {...sharedGridProps} ref={gridRef} rowData={activeData.ghostApproved} columnDefs={cols.ghostApproved} defaultColDef={defaultColDef} domLayout="autoHeight" pagination paginationPageSize={50} />
            </div>
          </Card>
        );

      case "orphans":
        return (
          <Card>
            <CardHeader
              right={
                <div className="flex items-center gap-2">
                  <ToggleGroup
                    value={orphanSub}
                    onChange={(v) => setOrphanSub(v as "mx" | "ronin")}
                    options={[
                      { id: "mx", label: `In ${procName}, Not in Ronin`, count: activeData.orphanMX.length, accent: "text-orange-700" },
                      { id: "ronin", label: `In Ronin, Not in ${procName}`, count: activeData.orphanRonin.length, accent: "text-indigo-700" },
                    ]}
                  />
                  {orphanSub === "mx" && activeData.orphanMX.length > 0 && selectedEventId !== "all" && (
                    <button
                      onClick={() => setShowSQLModal(true)}
                      className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-red-700 transition-colors flex items-center gap-1.5"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                      </svg>
                      Generate SQL
                    </button>
                  )}
                </div>
              }
            >
              Orphan Transactions
            </CardHeader>
            <TabDescription tab={activeTab} proc={procName} />
            <div className="ag-theme-quartz">
              {orphanSub === "mx"
                ? <AgGridReact {...sharedGridProps} ref={gridRef} rowData={activeData.orphanMX} columnDefs={cols.orphanMX} defaultColDef={defaultColDef} domLayout="autoHeight" pagination paginationPageSize={50} />
                : <AgGridReact {...sharedGridProps} ref={gridRef} rowData={activeData.orphanRonin} columnDefs={orphanRoninColumns} defaultColDef={defaultColDef} domLayout="autoHeight" pagination paginationPageSize={50} />
              }
            </div>
          </Card>
        );

      case "idMismatched":
        return (
          <Card>
            <CardHeader>ID Mismatched</CardHeader>
            <TabDescription tab={activeTab} proc={procName} />
            <div className="ag-theme-quartz">
              <AgGridReact {...sharedGridProps} ref={gridRef} rowData={activeData.idMismatched ?? []} columnDefs={cols.idMismatched} defaultColDef={defaultColDef} domLayout="autoHeight" pagination paginationPageSize={50} />
            </div>
          </Card>
        );

      case "duplicates":
        return (
          <Card>
            <CardHeader>Duplicate Orders</CardHeader>
            <TabDescription tab={activeTab} proc={procName} />
            <div className="px-4 py-2 text-xs text-gray-500 border-b border-gray-100">
              {duplicateOrders.length} duplicate orders — each <span className="text-red-600 font-medium">Void</span> order shares a PaymentIntent with the <span className="text-green-600 font-medium">Keep</span> order. Void the dup to remove the double-count.
            </div>
            <div className="ag-theme-quartz">
              <AgGridReact {...sharedGridProps} ref={gridRef} rowData={duplicateOrders} columnDefs={duplicateOrdersColumns} defaultColDef={defaultColDef} domLayout="autoHeight" pagination paginationPageSize={50} />
            </div>
          </Card>
        );

      case "doubleCharges": {
        const dc = activeData.doubleCharges ?? [];
        const toRefund = dc.reduce((s, d) => s + d.overcharge_cents, 0);
        return (
          <Card>
            <CardHeader>Double Charges</CardHeader>
            <TabDescription tab={activeTab} proc={procName} />
            <div className="px-4 py-2 text-xs text-gray-500 border-b border-gray-100">
              {dc.length} customer{dc.length === 1 ? " was" : "s were"} charged more than once — refund the duplicate PaymentIntent(s).
              {" "}Total to refund: <span className="font-mono font-medium text-red-600">{formatCents(toRefund)}</span>
            </div>
            <div className="ag-theme-quartz">
              <AgGridReact {...sharedGridProps} ref={gridRef} rowData={dc} columnDefs={doubleChargeColumns} defaultColDef={defaultColDef} domLayout="autoHeight" pagination paginationPageSize={50} />
            </div>
          </Card>
        );
      }

      case "refundMismatches": {
        const filteredRefunds = refundFilter === "mismatches" ? refundMismatches : allRefunds;
        return (
          <Card>
            <CardHeader
              right={
                <ToggleGroup
                  value={refundFilter}
                  onChange={(v) => setRefundFilter(v as "mismatches" | "all")}
                  options={[
                    { id: "mismatches", label: "Mismatches Only", count: refundMismatches.length, accent: "text-red-700" },
                    { id: "all", label: "All Refunds", count: allRefunds.length },
                  ]}
                />
              }
            >
              Refund Mismatches
            </CardHeader>
            <TabDescription tab={activeTab} proc={procName} />
            {refundFilter === "mismatches" && refundMismatches.length > 0 && (
              <div className="px-4 py-2 text-xs text-gray-500 border-b border-gray-100">
                {refundMismatches.length} orders marked as refund but payment still shows approved
              </div>
            )}
            {refundFilter === "all" && (
              <div className="px-4 py-2 text-xs text-gray-500 border-b border-gray-100">
                {allRefunds.length - refundMismatches.length} successful, {refundMismatches.length} broken
              </div>
            )}
            <div className="ag-theme-quartz">
              <AgGridReact {...sharedGridProps} ref={gridRef} rowData={filteredRefunds} columnDefs={cols.matched} defaultColDef={defaultColDef} domLayout="autoHeight" pagination paginationPageSize={50} />
            </div>
          </Card>
        );
      }

      case "voided":
        return (
          <Card>
            <CardHeader>Voided Orders</CardHeader>
            <TabDescription tab={activeTab} proc={procName} />
            <div className="ag-theme-quartz">
              <AgGridReact {...sharedGridProps} ref={gridRef} rowData={activeData.voidedOrders} columnDefs={cols.voided} defaultColDef={defaultColDef} domLayout="autoHeight" pagination paginationPageSize={50} />
            </div>
          </Card>
        );

      case "duplicateMX": {
        const dupRows = activeData.duplicateMX ?? [];
        const approvedRows = dupRows.filter(r => {
          const s = (r.mx_status || "").toLowerCase();
          return s.includes("approved") || s.includes("settled");
        });
        const declinedRows = dupRows.filter(r => {
          const s = (r.mx_status || "").toLowerCase();
          return s.includes("declined") || s.includes("error");
        });
        const otherRows = dupRows.filter(r => {
          const s = (r.mx_status || "").toLowerCase();
          return !s.includes("approved") && !s.includes("settled") && !s.includes("declined") && !s.includes("error");
        });
        const approvedTotal = approvedRows.reduce((sum, r) => sum + r.mx_amount_cents, 0);
        const declinedTotal = declinedRows.reduce((sum, r) => sum + r.mx_amount_cents, 0);
        const otherTotal = otherRows.reduce((sum, r) => sum + r.mx_amount_cents, 0);
        const allTotal = dupRows.reduce((sum, r) => sum + r.mx_amount_cents, 0);
        return (
          <Card>
            <CardHeader>Duplicate {procShort} Rows</CardHeader>
            <TabDescription tab={activeTab} proc={procName} />
            <div className="px-4 py-2 border-b border-gray-100 flex flex-wrap items-center gap-4 text-xs">
              <span className="font-medium text-gray-700">{dupRows.length} duplicate rows</span>
              <span className="text-gray-300">|</span>
              <span className="text-gray-600 tabular-nums">Total: <span className="font-mono font-medium">{formatCents(allTotal)}</span></span>
              {approvedRows.length > 0 && (
                <span className="text-blue-600 tabular-nums">Approved: <span className="font-mono font-medium">{formatCents(approvedTotal)}</span> ({approvedRows.length})</span>
              )}
              {declinedRows.length > 0 && (
                <span className="text-orange-600 tabular-nums">Declined: <span className="font-mono font-medium">{formatCents(declinedTotal)}</span> ({declinedRows.length})</span>
              )}
              {otherRows.length > 0 && (
                <span className="text-gray-500 tabular-nums">Other: <span className="font-mono font-medium">{formatCents(otherTotal)}</span> ({otherRows.length})</span>
              )}
            </div>
            <div className="ag-theme-quartz">
              <AgGridReact {...sharedGridProps} ref={gridRef} rowData={dupRows} columnDefs={cols.duplicateMX} defaultColDef={defaultColDef} domLayout="autoHeight" pagination paginationPageSize={50} />
            </div>
          </Card>
        );
      }

      case "statusMismatches":
        return (
          <Card>
            <CardHeader>Status Mismatches</CardHeader>
            <TabDescription tab={activeTab} proc={procName} />
            <div className="ag-theme-quartz">
              <AgGridReact {...sharedGridProps} ref={gridRef} rowData={statusMismatches} columnDefs={cols.statusMismatch} defaultColDef={defaultColDef} domLayout="autoHeight" pagination paginationPageSize={50} />
            </div>
          </Card>
        );

      default:
        return null;
    }
  };

  /* ---------------------------------------------------------------- */
  /* Page layout                                                      */
  /* ---------------------------------------------------------------- */

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{procName === "Processor" ? "Payment Processor" : procName} Reconciliation</h1>
          <p className="mt-1 text-sm text-gray-500">Upload payment processor CSV exports to reconcile against Ronin orders.</p>
        </div>
        <div className="flex gap-2">
          {activeData && (
            <button
              className="rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 shadow-sm hover:bg-gray-50 transition-colors"
              onClick={() => downloadFixItCsv(activeData, reconEventName ?? "all-events")}
              title="Export a consolidated list of every item that needs adjusting/deleting/adding, for ops to review"
            >
              Fix-It Export
            </button>
          )}
          {activeData && activeTab !== "summary" && (
            <button
              className="rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 shadow-sm hover:bg-gray-50 transition-colors"
              onClick={handleExport}
            >
              Export CSV
            </button>
          )}
          <button
            className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 transition-colors"
            onClick={() => setShowUpload(true)}
            disabled={loading}
          >
            {loading ? "Processing..." : (activeData || data) ? "Upload New Report" : "Upload Reports"}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <Card className="mt-4">
          <div className="px-4 py-3 text-sm text-red-700 bg-red-50">{error}</div>
        </Card>
      )}

      {/* Loading */}
      {loading && (
        <div className="mt-8 flex flex-col items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-red-600 border-t-transparent" />
          <p className="mt-3 text-sm text-gray-500">
            {multiProgress
              ? `Reconciling event ${multiProgress.current + 1} of ${multiProgress.total}...`
              : "Running reconciliation..."}
          </p>
        </div>
      )}

      {/* Results */}
      {activeData && !loading && (
        <div className="mt-6 space-y-4">
          {/* Event banner — single-event mode */}
          {!multiEventResults && reconEventName && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>Reconciling:</span>
              <span className="font-semibold text-gray-900">{reconEventName}</span>
              {reconProcessors.length > 0 && reconProcessors.map((p) => (
                <span key={p} className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-800 uppercase tracking-wide">{p}</span>
              ))}
            </div>
          )}

          {/* Event tab bar — multi-event mode */}
          {multiEventResults && multiEventResults.size > 0 && (
            <Card>
              <CardHeader
                right={
                  reconProcessors.length > 0 ? (
                    <div className="flex gap-1">
                      {reconProcessors.map((p) => (
                        <span key={p} className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-800 uppercase tracking-wide">{p}</span>
                      ))}
                    </div>
                  ) : undefined
                }
              >
                Events
              </CardHeader>
              <div className="px-4 overflow-x-auto">
                <nav className="-mb-px flex gap-1" aria-label="Event Tabs">
                  <button
                    className={classNames(
                      "whitespace-nowrap border-b-2 pb-2.5 pt-2.5 px-3 text-xs font-medium transition-colors",
                      selectedEventId === "all"
                        ? "border-blue-500 text-blue-600"
                        : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                    )}
                    onClick={() => setSelectedEventId("all")}
                  >
                    All Events
                    <span className={classNames(
                      "ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] tabular-nums",
                      selectedEventId === "all" ? "bg-blue-100 text-blue-600" : "bg-gray-100 text-gray-500"
                    )}>
                      {multiEventResults.size}
                    </span>
                  </button>
                  {Array.from(multiEventResults.entries()).map(([eventId, entry]) => {
                    const isActive = selectedEventId === eventId;
                    return (
                      <button
                        key={eventId}
                        className={classNames(
                          "whitespace-nowrap border-b-2 pb-2.5 pt-2.5 px-3 text-xs font-medium transition-colors",
                          isActive
                            ? "border-blue-500 text-blue-600"
                            : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                        )}
                        onClick={() => setSelectedEventId(eventId)}
                      >
                        {entry.eventName}
                        <span className={classNames(
                          "ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] tabular-nums",
                          isActive ? "bg-blue-100 text-blue-600" : "bg-gray-100 text-gray-500"
                        )}>
                          {entry.data.matched.length.toLocaleString()}
                        </span>
                      </button>
                    );
                  })}
                </nav>
              </div>
            </Card>
          )}

          {/* Sub-tab bar */}
          <Card>
            <div className="px-4 overflow-x-auto">
              <nav className="-mb-px flex gap-1" aria-label="Tabs">
                {TABS.filter((tab) => tab.id !== "duplicateMX" || (activeData?.duplicateMX?.length ?? 0) > 0).map((tab) => {
                  const badge = badgeCounts[tab.id];
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      className={classNames(
                        "whitespace-nowrap border-b-2 pb-2.5 pt-2.5 px-3 text-xs font-medium transition-colors",
                        isActive
                          ? "border-red-500 text-red-600"
                          : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                      )}
                      onClick={() => setActiveTab(tab.id)}
                    >
                      {tab.id === "duplicateMX" ? `Duplicate ${procShort} Rows` : tab.label}
                      {badge != null && (
                        <span className={classNames(
                          "ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] tabular-nums",
                          isActive ? "bg-red-100 text-red-600" : "bg-gray-100 text-gray-500"
                        )}>
                          {badge.toLocaleString()}
                        </span>
                      )}
                    </button>
                  );
                })}
              </nav>
            </div>
          </Card>

          {/* Active tab content */}
          {renderGrid()}
        </div>
      )}

      {/* Empty state */}
      {!activeData && !loading && !error && (
        <div className="mt-12">
          <Card>
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <svg className="h-12 w-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m6.75 12H9.75m3 0h-3m-2.25 0H5.625c-.621 0-1.125-.504-1.125-1.125V4.125C4.5 3.504 5.004 3 5.625 3h5.25M19.5 14.25v4.125c0 .621-.504 1.125-1.125 1.125H5.625c-.621 0-1.125-.504-1.125-1.125V4.125C4.5 3.504 5.004 3 5.625 3" />
              </svg>
              <h3 className="mt-4 text-sm font-medium text-gray-900">No reconciliation data</h3>
              <p className="mt-1 text-sm text-gray-500">Upload payment processor CSV exports and select an event to get started.</p>
            </div>
          </Card>
        </div>
      )}

      <MXUploadModal open={showUpload} onClose={() => setShowUpload(false)} onUpload={handleUpload} onUploadAll={handleUploadAll} />

      {/* Single-event SQL modal */}
      {data && reconEventId && !multiEventResults && (
        <GenerateSQLModal
          open={showSQLModal}
          onClose={() => setShowSQLModal(false)}
          orphanMX={data.orphanMX}
          eventId={reconEventId}
          eventName={reconEventName ?? `Event ${reconEventId}`}
          processorLabel={procName}
        />
      )}
      {/* Multi-event SQL modal — only when viewing a specific event */}
      {multiEventResults && typeof selectedEventId === "number" && multiEventResults.has(selectedEventId) && (
        <GenerateSQLModal
          open={showSQLModal}
          onClose={() => setShowSQLModal(false)}
          orphanMX={multiEventResults.get(selectedEventId)!.data.orphanMX}
          eventId={selectedEventId}
          eventName={multiEventResults.get(selectedEventId)!.eventName}
          processorLabel={procName}
        />
      )}
    </div>
  );
}